import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { getVoyageList, getVoyagesByVessel, getVesselOperations, getCreatedReportById, getDprList, getCargoOperations, getTemplateList } from '../../../core/_requests'
import { searchDefects } from '../../../../QHSE/core/_requests'
import { useAuth } from '../../../../auth'
import { useGlobalFilter } from '../../../../../shared/context/GlobalFilterContext'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../../../shared/access/accessibleVessels'
import { getApiErrorMessage } from '../../../../../shared/utils/apiErrorMessage'
import { useOperationsAccess } from '../../../core/useOperationsAccess'
import { OP } from '../../../core/operationPermissions'
import { isOpenDashboardDefect, isOverdueDashboardDefect, normalizeDashboardStatus, recordedCiiDistribution, scopeDashboardVessels } from '../dashboardData'
import type { OperationsDashboardData, OperationsDerivedKpis, VesselStatusRow } from '../types'

const emptyData = (): OperationsDashboardData => ({
  voyages: [], vessels: [], vesselOps: null, createdReports: [], dprList: [], cargoOps: [],
  templates: [], ciiRuleSet: null, defects: [], statusByVessel: {}, recordedCii: [],
})

export function useOperationsDashboard() {
  const { currentUser } = useAuth()
  const { state } = useGlobalFilter()
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'ALL' | 'AT_SEA' | 'IN_PORT' | 'AT_ANCHOR'>('ALL')
  const [snapshot, setSnapshot] = useState<{ data: OperationsDashboardData; errors: Record<string, string>; fetchedAt: Date | null }>({ data: emptyData(), errors: {}, fetchedAt: null })
  const requestId = useRef(0)
  const companyId = state.company?.id
  const companyType = state.company?.type
  const vesselId = state.vessel?.id
  const search = state.searchQuery

  const fetchAll = useCallback(async () => {
    const id = ++requestId.current
    setLoading(true)
    if (!permissionsLoaded) return
    const errors: Record<string, string> = {}
    const data = emptyData()
    const load = async <T,>(key: string, permission: string | null, fetch: () => Promise<T>, fallback: T): Promise<T> => {
      if (permission && !opCan(permission)) {
        errors[key] = 'You do not have permission to view this data.'
        return fallback
      }
      try {
        const result = await fetch()
        if (Array.isArray(fallback) && !Array.isArray(result)) throw new Error('The server returned an invalid list. Retry loading this data.')
        return result
      } catch (error) {
        errors[key] = getApiErrorMessage(error, 'Could not load this data. Retry the dashboard refresh.')
        return fallback
      }
    }
    const accessible = await load('vessels', null, fetchAccessibleVesselsForCurrentUser, [])
    data.vessels = scopeDashboardVessels(normalizeAccessibleVessels(accessible), companyId && companyType ? { id: companyId, type: companyType, name: '' } : null, vesselId, search)
    if (!errors.vessels) {
      const vesselIds = new Set(data.vessels.map(v => Number(v.id)))
      const vesselNames = new Map(data.vessels.map(v => [Number(v.id), v.fleet_name]))
      const inScope = (row: any) => vesselIds.has(Number(row.vesselId ?? row.vessel?.id))
      // Search is vessel name/IMO across every dataset, not a cargo-only text filter.
      const hasScope = vesselIds.size > 0
      const cargoPages = async () => {
        const rows: any[] = []
        let page = 0
        let totalPages = 1
        do {
          const response = await getCargoOperations({ page, size: 100, ...(vesselId ? { vesselId } : {}) })
          if (!Array.isArray(response.content)) throw new Error('The cargo list response is invalid.')
          rows.push(...response.content)
          totalPages = response.totalPages
          if (!Number.isInteger(totalPages) || totalPages < 0) throw new Error('The cargo pagination response is invalid.')
          page += 1
        } while (page < totalPages)
        return rows
      }
      const [voyages, reports, dpr, cargo, templates, defects] = await Promise.all([
        load('voyages', OP.READ_VOYAGE, () => hasScope ? vesselId ? getVoyagesByVessel(vesselId) : getVoyageList() : Promise.resolve([]), []),
        load('reports', OP.READ_POSITION_REPORT, () => hasScope ? getCreatedReportById(vesselId) : Promise.resolve([]), []),
        load('dpr', OP.READ_DAILY_PROGRESS_REPORT, () => hasScope ? getDprList() : Promise.resolve([]), []),
        load('cargo', OP.READ_CARGO_OPERATION, () => hasScope ? cargoPages() : Promise.resolve([]), []),
        load('templates', 'READ_REPORT_TEMPLATE', getTemplateList, []),
        load('defects', 'READ_QHSE_DEFECT', () => hasScope ? searchDefects({ activeOnly: true, ...(vesselId ? { vesselId } : {}) }) : Promise.resolve([]), []),
      ])
      data.voyages = voyages.filter(inScope)
      data.createdReports = reports.filter(inScope).map(report => ({
        ...report,
        vesselName: vesselNames.get(Number(report.vesselId ?? report.vessel?.id)) || report.vesselName || 'Vessel name unavailable',
      }))
      data.dprList = dpr.filter(inScope).map(row => ({ ...row, activityCount: Array.isArray(row.activities) ? row.activities.length : null }))
      data.cargoOps = cargo.filter(inScope)
      data.templates = templates
      data.defects = defects.filter(inScope)
      if (!opCan(OP.READ_CII_SUMMARY)) errors.cii = 'You do not have permission to view this data.'
      for (let offset = 0; offset < data.vessels.length; offset += 6) {
        if (requestId.current !== id) return
        await Promise.all(data.vessels.slice(offset, offset + 6).map(async vessel => {
          const status = await load(`status:${vessel.id}`, OP.READ_POSITION_REPORT, () => getVesselOperations(vessel.id), null)
          if (status) data.statusByVessel[vessel.id] = status
        }))
      }
    }
    if (requestId.current !== id) return
    setSnapshot({ data, errors, fetchedAt: new Date() })
    setLoading(false)
  }, [companyId, companyType, vesselId, search, currentUser, permissionsLoaded, opCan])

  useEffect(() => {
    void fetchAll()
    return () => { requestId.current += 1 }
  }, [fetchAll])

  const { data, errors, fetchedAt } = snapshot
  const vesselStatusRows: VesselStatusRow[] = useMemo(() => data.voyages.filter(v => v.active).flatMap(voyage => {
    const vessel = data.vessels.find(v => Number(v.id) === Number(voyage.vessel?.id))
    if (!vessel) return []
    const status = data.statusByVessel[vessel.id]
    const latestDpr = data.dprList.filter(d => Number(d.vesselId) === Number(vessel.id) && ['SUBMITTED', 'REVIEWED'].includes(d.status))
      .sort((a, b) => String(b.reportDate).localeCompare(String(a.reportDate)))[0]
    const reported = normalizeDashboardStatus(status?.status)
    const clean = (value: any) => value && value !== '--:--' ? String(value) : ''
    return {
      vessel: vessel.fleet_name, vesselId: vessel.id,
      status: reported !== 'Unknown' ? reported : normalizeDashboardStatus(latestDpr?.portOrSea),
      activity: voyage ? `${voyage.departurePort || '—'} → ${voyage.arrivalPort || '—'}` : 'Not reported',
      nextPort: clean(status?.nextPort) || voyage?.arrivalPort || '—',
      eta: clean(status?.eta) || voyage?.eta || '—',
      risk: 'Not assessed', context: voyage?.loadingCondition || 'Not reported', voyageId: voyage.id, voyageNumber: voyage.voyageNumber,
    }
  }), [data])

  const kpis: OperationsDerivedKpis = useMemo(() => {
    const active = data.voyages.filter(v => v.active)
    const open = data.defects.filter(isOpenDashboardDefect)
    const cii = recordedCiiDistribution(data.recordedCii)
    const draft = data.createdReports.filter(r => String(r.status).toUpperCase() === 'DRAFT')
    const today = new Date().toISOString().slice(0, 10)
    return {
      activeVoyages: active.length, totalVoyages: data.voyages.length,
      vesselsAtSea: new Set(vesselStatusRows.filter(r => r.status === 'At Sea').map(r => r.vesselId)).size,
      vesselsInPort: new Set(vesselStatusRows.filter(r => r.status === 'In Port').map(r => r.vesselId)).size,
      vesselsAtAnchor: new Set(vesselStatusRows.filter(r => r.status === 'At Anchorage').map(r => r.vesselId)).size,
      vesselsUnknown: new Set(vesselStatusRows.filter(r => r.status === 'Unknown').map(r => r.vesselId)).size,
      openDefects: open.length, criticalDefects: 0,
      overdueDefects: open.filter(d => isOverdueDashboardDefect(d)).length,
      ciiRatingDistribution: cii.counts, ciiRating: cii.rating, ciiRatedCount: cii.rated,
      vesselsRatedDOrBelow: cii.counts.d + cii.counts.e,
      draftDprReports: data.dprList.filter(d => d.status === 'DRAFT').length,
      overdueReports: draft.filter(r => r.createdDateTime && Date.now() - new Date(r.createdDateTime).getTime() > 86400000).length,
      totalDprToday: data.dprList.filter(d => d.reportDate?.startsWith(today) && ['SUBMITTED', 'REVIEWED'].includes(d.status)).length,
      dprOverdue: data.dprList.filter(d => d.reportDate?.startsWith(today) && d.status === 'DRAFT').length,
      activeCargoOps: data.cargoOps.length,
      tankCargoCount: data.cargoOps.filter(c => c.breakupType === 'TANK').length,
      holdCargoCount: data.cargoOps.filter(c => c.breakupType === 'HOLD').length,
    }
  }, [data, vesselStatusRows])

  const sourceError = (key: string) => errors.vessels || errors[key] || (key === 'status' ? errors.voyages : undefined) || (['status', 'cii'].includes(key) ? Object.keys(errors).filter(k => k.startsWith(`${key}:`)).map(k => errors[k])[0] : undefined)
  return { loading, data, kpis, vesselStatusRows, activeTab, setActiveTab, refresh: fetchAll, errors, sourceError, fetchedAt }
}
